import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, OptionSelect } from "@/components/FormBits";
import { ConfirmAction } from "@/components/ConfirmAction";
import { api, formatApiError } from "@/lib/api";
import { useFacilities } from "@/hooks/useFacilities";
import { APPLICANT_TYPES, BOOKING_STATUS, PAYMENT_STATUS, TARIFF_TYPES, FACILITY_STATUS, todayISO } from "@/lib/constants";

const EMPTY = {
  facility_slug: "", title: "", event_type: "", date: "", start_time: "09:00", end_time: "13:00",
  applicant_name: "", applicant_type: "privato", applicant_contact: "", applicant_tax_code: "", attendees: "",
  tariff_type: "oraria", amount: 0, payment_status: "da_pagare", payment_reference: "", payment_date: "",
  booking_status: "confermata", notes: "",
};

const hoursBetween = (a, b) => {
  const [h1, m1] = a.split(":").map(Number);
  const [h2, m2] = b.split(":").map(Number);
  return Math.max(0, (h2 * 60 + m2 - h1 * 60 - m1) / 60);
};

function computeAmount(f, fac) {
  if (f.tariff_type === "gratuita") return 0;
  if (!fac || f.tariff_type === "forfait") return null;
  if (f.tariff_type === "giornaliera") return fac.daily_rate;
  return Math.round(hoursBetween(f.start_time, f.end_time) * fac.hourly_rate * 100) / 100;
}

export const BookingDialog = ({ open, onOpenChange, booking, defaults, onSaved }) => {
  const [facilities, reloadFacilities] = useFacilities();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    reloadFacilities();
    if (booking) setForm({ ...EMPTY, ...booking, attendees: booking.attendees ?? "", payment_date: booking.payment_date || "" });
    else setForm({ ...EMPTY, date: todayISO(), ...defaults });
  }, [open, booking, defaults, reloadFacilities]);

  const update = (patch) => setForm((f) => {
    const n = { ...f, ...patch };
    if (["facility_slug", "tariff_type", "start_time", "end_time"].some((k) => k in patch)) {
      const a = computeAmount(n, facilities.find((x) => x.slug === n.facility_slug));
      if (a !== null) n.amount = a;
      if (patch.tariff_type === "gratuita") n.payment_status = "esente";
    }
    return n;
  });

  const facOptions = Object.fromEntries(facilities.map((f) => [f.slug, f.name]));
  const selected = facilities.find((f) => f.slug === form.facility_slug);

  const save = async (e) => {
    e.preventDefault();
    if (!form.facility_slug) return toast.error("Seleziona una struttura");
    setSaving(true);
    const payload = { ...form, amount: Number(form.amount) || 0, attendees: form.attendees === "" ? null : Number(form.attendees), payment_date: form.payment_date || null };
    try {
      if (booking) await api.put(`/bookings/${booking.id}`, payload);
      else await api.post("/bookings", payload);
      toast.success(booking ? "Prenotazione aggiornata" : "Prenotazione registrata");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/bookings/${booking.id}`);
      toast.success("Prenotazione eliminata");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const txt = (k) => ({ value: form[k], onChange: (e) => update({ [k]: e.target.value }) });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="booking-modal" className="max-w-3xl max-h-[92vh] overflow-y-auto bg-[#FBFAF7]">
        <DialogHeader>
          <p className="eyebrow">{booking ? "Modifica pratica" : "Nuova pratica di concessione"}</p>
          <DialogTitle className="font-serif text-3xl font-semibold text-slate-900">
            {booking ? booking.title : "Nuova prenotazione"}
          </DialogTitle>
          <DialogDescription>Compila i dati della concessione. L'importo viene calcolato dalla tariffa e resta modificabile.</DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-6">
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Struttura" className="sm:col-span-2">
              <OptionSelect testId="booking-facility-select" value={form.facility_slug} onChange={(v) => update({ facility_slug: v })} options={facOptions} placeholder="Seleziona struttura" />
              {selected && selected.status !== "disponibile" && (
                <p data-testid="booking-facility-warning" className="mt-1 flex items-center gap-1.5 text-xs text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5" /> Attenzione: struttura attualmente "{FACILITY_STATUS[selected.status].label}"
                </p>
              )}
            </Field>
            <Field label="Titolo evento"><Input data-testid="booking-title-input" required {...txt("title")} placeholder="Es. Matrimonio Rossi - Bianchi" /></Field>
            <Field label="Tipologia"><Input data-testid="booking-event-type-input" {...txt("event_type")} placeholder="Convegno, spettacolo, matrimonio…" /></Field>
            <Field label="Data"><Input data-testid="booking-date-input" type="date" required {...txt("date")} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Dalle"><Input data-testid="booking-start-input" type="time" required {...txt("start_time")} /></Field>
              <Field label="Alle"><Input data-testid="booking-end-input" type="time" required {...txt("end_time")} /></Field>
            </div>
          </section>

          <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-slate-200 pt-5">
            <Field label="Richiedente"><Input data-testid="booking-applicant-input" required {...txt("applicant_name")} placeholder="Nome e cognome / ragione sociale" /></Field>
            <Field label="Tipo richiedente"><OptionSelect testId="booking-applicant-type-select" value={form.applicant_type} onChange={(v) => update({ applicant_type: v })} options={APPLICANT_TYPES} /></Field>
            <Field label="Contatto (tel. / email)"><Input data-testid="booking-contact-input" {...txt("applicant_contact")} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="C.F. / P.IVA"><Input data-testid="booking-taxcode-input" {...txt("applicant_tax_code")} /></Field>
              <Field label="Partecipanti"><Input data-testid="booking-attendees-input" type="number" min="0" {...txt("attendees")} /></Field>
            </div>
          </section>

          <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-slate-200 pt-5">
            <Field label="Tariffa"><OptionSelect testId="booking-tariff-select" value={form.tariff_type} onChange={(v) => update({ tariff_type: v })} options={TARIFF_TYPES} /></Field>
            <Field label="Importo (€)"><Input data-testid="booking-amount-input" type="number" step="0.01" min="0" className="font-mono" {...txt("amount")} /></Field>
            <Field label="Stato pagamento"><OptionSelect testId="booking-payment-select" value={form.payment_status} onChange={(v) => update({ payment_status: v })} options={PAYMENT_STATUS_LABELS} /></Field>
            <Field label="Rif. pagamento (IUV / reversale)"><Input data-testid="booking-payment-ref-input" {...txt("payment_reference")} /></Field>
            <Field label="Data pagamento"><Input data-testid="booking-payment-date-input" type="date" {...txt("payment_date")} /></Field>
            <Field label="Stato pratica"><OptionSelect testId="booking-status-select" value={form.booking_status} onChange={(v) => update({ booking_status: v })} options={BOOKING_STATUS_LABELS} /></Field>
            {selected && (
              <p className="sm:col-span-3 font-mono text-xs text-slate-500" data-testid="booking-tariff-hint">
                Tariffe {selected.name}: {selected.hourly_rate} €/ora · {selected.daily_rate} €/giorno
              </p>
            )}
          </section>

          <Field label="Note interne"><Textarea data-testid="booking-notes-input" rows={3} {...txt("notes")} /></Field>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 border-t border-slate-200 pt-5">
            {booking && (
              <ConfirmAction
                testId="booking-delete"
                title="Eliminare la prenotazione?"
                description="L'operazione è definitiva. Per mantenere lo storico puoi invece impostare lo stato su Annullata."
                confirmLabel="Elimina"
                onConfirm={remove}
                trigger={<Button type="button" variant="ghost" data-testid="booking-delete-button" className="text-red-700 hover:bg-red-50 hover:text-red-800"><Trash2 className="h-4 w-4 mr-2" />Elimina</Button>}
              />
            )}
            <div className="sm:ml-auto flex gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="booking-modal-cancel-button">Annulla</Button>
              <Button type="submit" disabled={saving} data-testid="booking-modal-save-button" className="bg-[#8B1E2B] hover:bg-[#701621]">
                {saving ? "Salvataggio…" : "Salva prenotazione"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PAYMENT_STATUS_LABELS = Object.fromEntries(Object.entries(PAYMENT_STATUS).map(([k, v]) => [k, v.label]));
const BOOKING_STATUS_LABELS = Object.fromEntries(Object.entries(BOOKING_STATUS).map(([k, v]) => [k, v.label]));
